"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useRef, useState, type FocusEvent, type ReactNode } from "react";
import Image from "next/image";
import type { StaticImageData } from "next/image";
import Link from "next/link";
import { ChevronDown, ChevronUp, Menu, X } from "lucide-react";
import { Typography } from "./typography";
import { linkTargetProps, type DiscoveryHeaderView, type DiscoveryNavItemView } from "@repo/sanity-utils/discoveryChrome";
import { trackDiscoveryLinkClick } from "@repo/tracking/discovery/lib/discovery-link-tracking";
import { DiscoveryMobileMenuCloseProvider } from "./DiscoveryMobileMenuClose";
import { DiscoveryNavLinkIcon } from "./DiscoveryNavLinkIcon";
import styles from "./DiscoveryHeaderNav.module.css";
type Props = {
  /** Required when `isLoading` is not set. */
  data?: DiscoveryHeaderView;
  fallbackLogoSrc: string | StaticImageData;
  fallbackLogoHref: string;
  /** Optional DOM id, used by 404 canvas sizing and skip-link targets. */
  id?: string;
  /** Optional trailing chrome (e.g. aops Sign in / account menu). */
  actions?: ReactNode;
  /** Optional content rendered inside the mobile menu (e.g. Sign in / Sign up). */
  mobileActions?: ReactNode;
  /**
   * Rendered at the bottom of the hamburger list. Hidden with that list at
   * the desktop breakpoint.
   */
  mobileMenu?: ReactNode;
  /** Optional extra click handler after Discovery funnel tracking. */
  onNavLinkClick?: (href: string) => void;
  /**
   * Suspense fallback: same layout CSS as the loaded header, but inert and
   * `aria-hidden` so keyboard / AT users cannot land on an empty nav.
   */
  isLoading?: boolean;
};
const HEADER_SKELETON_TOP_LINKS = 5;
const HEADER_SKELETON_TABS = 5;
function DiscoveryHeaderNavSkeleton(t0) {
  const $ = _c(10);
  const {
    id,
    fallbackLogoSrc
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={styles.topLinkBar}><ul className={styles.topLinkList} role="presentation">{Array.from({
          length: HEADER_SKELETON_TOP_LINKS
        }, _temp)}</ul></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== fallbackLogoSrc) {
    t2 = <span className={styles.logoLink}><Image src={fallbackLogoSrc} alt="" width={142} height={32} className={styles.logo} loading="eager" unoptimized={true} /></span>;
    $[1] = fallbackLogoSrc;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={styles.desktopNav}><ul className={styles.tabList} role="presentation">{Array.from({
          length: HEADER_SKELETON_TABS
        }, _temp2)}</ul></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className={styles.actions}><span className={styles.mobileToggle} /></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2) {
    t5 = <div className={styles.bar}>{t2}{t3}{t4}</div>;
    $[5] = t2;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== id || $[8] !== t5) {
    t6 = <header className={`${styles.header} ${styles.loading}`} id={id} aria-hidden={true} aria-busy="true" inert={true}>{t1}{t5}</header>;
    $[7] = id;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function _temp2(__0, index_0) {
  return <li key={index_0}><span className={styles.tab}><Typography as="span" variant="buttonMd"><span className={`${styles.skeletonBone} ${styles.skeletonTab}`} /></Typography></span></li>;
}
function _temp(_, index) {
  return <li key={index}><span className={styles.topLink}><Typography as="span" variant="caption"><span className={`${styles.skeletonBone} ${styles.skeletonTopLink}`} /></Typography></span></li>;
}
function hasChildren(item: DiscoveryNavItemView) {
  return item.children.length > 0;
}
export function DiscoveryHeaderNav(t0) {
  const $ = _c(63);
  const {
    data,
    fallbackLogoSrc,
    fallbackLogoHref,
    id,
    actions,
    mobileActions,
    mobileMenu,
    onNavLinkClick,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== data?.items) {
    t2 = data?.items ?? [];
    $[0] = data?.items;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const items = t2;
  let t3;
  if ($[2] !== data?.topLinks) {
    t3 = data?.topLinks ?? [];
    $[2] = data?.topLinks;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const topLinks = t3;
  const logoHref = data?.logoHref || fallbackLogoHref;
  const logoAlt = data?.logoAlt || "Art of Problem Solving";
  const logoSrc = data?.logoUrl || fallbackLogoSrc;
  let t4;
  if ($[4] !== onNavLinkClick) {
    t4 = function reportNavLinkClick(href) {
      trackDiscoveryLinkClick(href, "header_nav");
      onNavLinkClick?.(href);
    };
    $[4] = onNavLinkClick;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const reportNavLinkClick = t4;
  const [openKey, setOpenKey] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const menuId = useId();
  let t5;
  let t6;
  if ($[6] !== openKey) {
    t5 = () => {
      if (!openKey) {
        return;
      }
      const onPointerDown = event => {
        if (navRef.current && !navRef.current.contains(event.target as Node)) {
          setOpenKey(null);
        }
      };
      const onKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          setOpenKey(null);
        }
      };
      document.addEventListener("pointerdown", onPointerDown);
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.removeEventListener("pointerdown", onPointerDown);
        document.removeEventListener("keydown", onKeyDown);
      };
    };
    t6 = [openKey];
    $[6] = openKey;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== mobileOpen) {
    t7 = () => {
      if (!mobileOpen) {
        return;
      }
      const onKeyDown_0 = event_1 => {
        if (event_1.key !== "Escape") {
          return;
        }
        setMobileOpen(false);
        mobileToggleRef.current?.focus();
      };
      document.addEventListener("keydown", onKeyDown_0);
      return () => document.removeEventListener("keydown", onKeyDown_0);
    };
    t8 = [mobileOpen];
    $[9] = mobileOpen;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = function closeDropdownIfFocusLeft(itemKey, event_2) {
      const next = event_2.relatedTarget;
      if (!(next instanceof Node)) {
        return;
      }
      if (event_2.currentTarget.contains(next)) {
        return;
      }
      setOpenKey(key => key === itemKey ? null : key);
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const closeDropdownIfFocusLeft = t9;
  if (isLoading) {
    let t10;
    if ($[13] !== fallbackLogoSrc || $[14] !== id) {
      t10 = <DiscoveryHeaderNavSkeleton id={id} fallbackLogoSrc={fallbackLogoSrc} />;
      $[13] = fallbackLogoSrc;
      $[14] = id;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    return t10;
  }
  let t10;
  if ($[16] !== topLinks) {
    t10 = topLinks.length > 0 && <nav className={styles.topLinkBar} aria-label="Sites"><ul className={styles.topLinkList} role="list">{topLinks.map(_temp3)}</ul></nav>;
    $[16] = topLinks;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== logoAlt || $[19] !== logoSrc) {
    t11 = <Image src={logoSrc} alt={logoAlt} width={142} height={32} className={styles.logo} loading="eager" unoptimized={true} />;
    $[18] = logoAlt;
    $[19] = logoSrc;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== logoHref || $[22] !== t11) {
    t12 = <Link href={logoHref} className={styles.logoLink}>{t11}</Link>;
    $[21] = logoHref;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== items || $[25] !== menuId || $[26] !== openKey || $[27] !== reportNavLinkClick) {
    let t14;
    if ($[29] !== menuId || $[30] !== openKey || $[31] !== reportNavLinkClick) {
      t14 = item => {
        const children = hasChildren(item);
        const isOpen = openKey === item._key;
        if (!children) {
          const {
            href: href_0
          } = item;
          if (!href_0) {
            return null;
          }
          return <li key={item._key}><a href={href_0} className={styles.tab} {...linkTargetProps(item.openInNewTab)} onClick={() => reportNavLinkClick(href_0)}><Typography as="span" variant="buttonMd">{item.label}</Typography></a></li>;
        }
        return <li key={item._key} className={styles.tabItem} onMouseEnter={() => setOpenKey(item._key)} onMouseLeave={() => setOpenKey(key_0 => key_0 === item._key ? null : key_0)} onBlur={event_3 => closeDropdownIfFocusLeft(item._key, event_3)}><button type="button" className={`${styles.tab} ${isOpen ? styles.tabActive : ""}`} aria-expanded={isOpen} aria-controls={`${menuId}-${item._key}`} onClick={() => setOpenKey(isOpen ? null : item._key)}><Typography as="span" variant="buttonMd">{item.label}</Typography>{isOpen ? <ChevronUp size={16} strokeWidth={1.5} aria-hidden={true} /> : <ChevronDown size={16} strokeWidth={1.5} aria-hidden={true} />}</button>{isOpen && <div id={`${menuId}-${item._key}`} className={styles.dropdown} role="region" aria-label={item.label}>{item.panelGroupLabel && <Typography as="p" variant="eyebrow" className={styles.panelGroupLabel}>{item.panelGroupLabel}</Typography>}<ul className={styles.dropdownList} role="list">{item.children.map(child => <li key={child._key}><a href={child.href} className={styles.dropdownLink} {...linkTargetProps(child.openInNewTab)} onClick={() => reportNavLinkClick(child.href)}><DiscoveryNavLinkIcon link={child} variant="tile" /><span className={styles.dropdownText}><Typography as="span" variant="buttonMd" className={styles.dropdownLabel}>{child.label}</Typography>{child.description && <Typography as="span" variant="bodySmall" className={styles.dropdownDescription}>{child.description}</Typography>}</span></a></li>)}</ul></div>}</li>;
      };
      $[29] = menuId;
      $[30] = openKey;
      $[31] = reportNavLinkClick;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    t13 = items.map(t14);
    $[24] = items;
    $[25] = menuId;
    $[26] = openKey;
    $[27] = reportNavLinkClick;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[33] !== t13) {
    t14 = <nav className={styles.desktopNav} aria-label="Primary"><ul className={styles.tabList} role="list">{t13}</ul></nav>;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = `${menuId}-mobile`;
  const t16 = mobileOpen ? "Close menu" : "Open menu";
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setMobileOpen(_temp4);
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== mobileOpen) {
    t18 = mobileOpen ? <X size={24} strokeWidth={1.5} aria-hidden={true} /> : <Menu size={24} strokeWidth={1.5} aria-hidden={true} />;
    $[36] = mobileOpen;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== mobileOpen || $[39] !== t15 || $[40] !== t16 || $[41] !== t18) {
    t19 = <button ref={mobileToggleRef} type="button" className={styles.mobileToggle} aria-expanded={mobileOpen} aria-controls={t15} aria-label={t16} onClick={t17}>{t18}</button>;
    $[38] = mobileOpen;
    $[39] = t15;
    $[40] = t16;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== actions || $[44] !== t19) {
    t20 = <div className={styles.actions}>{actions}{t19}</div>;
    $[43] = actions;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t12 || $[47] !== t14 || $[48] !== t20) {
    t21 = <div className={styles.bar}>{t12}{t14}{t20}</div>;
    $[46] = t12;
    $[47] = t14;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== items || $[51] !== menuId || $[52] !== mobileActions || $[53] !== mobileMenu || $[54] !== mobileOpen || $[55] !== openKey || $[56] !== reportNavLinkClick) {
    t22 = mobileOpen && <nav id={`${menuId}-mobile`} className={styles.mobileNav} aria-label="Primary"><DiscoveryMobileMenuCloseProvider value={() => setMobileOpen(false)}><ul className={styles.mobileList} role="list">{items.map(item_0 => {
            if (hasChildren(item_0)) {
              const isOpen_0 = openKey === item_0._key;
              return <li key={item_0._key} className={styles.mobileItem}><button type="button" className={styles.mobileItemButton} aria-expanded={isOpen_0} onClick={() => setOpenKey(isOpen_0 ? null : item_0._key)}><Typography as="span" variant="h5">{item_0.label}</Typography><ChevronDown size={24} strokeWidth={1.5} aria-hidden={true} className={isOpen_0 ? styles.chevronOpen : undefined} /></button>{isOpen_0 && <div className={styles.mobilePanel}>{item_0.panelGroupLabel && <Typography as="p" variant="eyebrow" className={styles.panelGroupLabel}>{item_0.panelGroupLabel}</Typography>}<ul className={styles.mobileSubList} role="list">{item_0.children.map(child_0 => <li key={child_0._key}><a href={child_0.href} className={styles.mobileSubLink} {...linkTargetProps(child_0.openInNewTab)} onClick={() => {
                        reportNavLinkClick(child_0.href);
                        setMobileOpen(false);
                      }}><DiscoveryNavLinkIcon link={child_0} variant="tile" /><span className={styles.dropdownText}><Typography as="span" variant="buttonMd">{child_0.label}</Typography>{child_0.description && <Typography as="span" variant="bodySmall" className={styles.dropdownDescription}>{child_0.description}</Typography>}</span></a></li>)}</ul></div>}</li>;
            }
            const {
              href: href_1
            } = item_0;
            if (!href_1) {
              return null;
            }
            return <li key={item_0._key} className={styles.mobileItem}><a href={href_1} className={styles.mobileDirectLink} {...linkTargetProps(item_0.openInNewTab)} onClick={() => {
                reportNavLinkClick(href_1);
                setMobileOpen(false);
              }}><Typography as="span" variant="h5">{item_0.label}</Typography></a></li>;
          })}{mobileMenu}</ul>{mobileActions ? <div className={styles.mobileActions}>{mobileActions}</div> : null}</DiscoveryMobileMenuCloseProvider></nav>;
    $[50] = items;
    $[51] = menuId;
    $[52] = mobileActions;
    $[53] = mobileMenu;
    $[54] = mobileOpen;
    $[55] = openKey;
    $[56] = reportNavLinkClick;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== id || $[59] !== t10 || $[60] !== t21 || $[61] !== t22) {
    t23 = <header className={styles.header} ref={navRef} id={id}>{t10}{t21}{t22}</header>;
    $[58] = id;
    $[59] = t10;
    $[60] = t21;
    $[61] = t22;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  return t23;
}
function _temp4(open) {
  return !open;
}
function _temp3(link) {
  return <li key={link._key}><a href={link.href} className={[styles.topLink, link.iconUrl ? styles.topLinkWithIcon : "", link.isCurrent ? styles.topLinkCurrent : ""].filter(Boolean).join(" ")} aria-label={link.iconUrl ? link.label : undefined} aria-current={link.isCurrent ? "page" : undefined} {...linkTargetProps(link.openInNewTab)}>{link.iconUrl && <span className={styles.topLinkIcon}><Image src={link.iconUrl} alt="" aria-hidden={true} width={20} height={20} unoptimized={true} /></span>}<Typography as="span" variant="caption" className={styles.topLinkLabel}>{link.label}</Typography></a></li>;
}